import { useMemo } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';

import { useTheme } from '#/hooks/useTheme';
import { cn } from '#/lib/utils';
import {
  DASHBOARD_CHART_CLASS,
  formatCount,
  getStatusColor,
} from '#/modules/infra/dashboard/helper/dashboardChartTheme';

/**
 * 月別狀態圖（ApexCharts Stacked Column）：每月一根柱子，柱頂標總筆數，柱內依狀態分段。
 * 只有一個月時改為單條橫向 100% 比例條（段內標筆數），不畫一排獨立柱子。
 * 直接使用 response `status_by_month` 的 categories／series，前端不自行彙總。
 * 顏色用狀態卡同一套語意色；圖例沿用頁首 DashboardToneLegend，圖內不另畫。tooltip 只列筆數 > 0 的狀態。
 */
const StatusByMonthChart = ({
  statusByMonth,
  statusLabels,
  statusOrder,
  statusTones,
  onBarClick,
  height = 320,
}) => {
  const { resolvedTheme } = useTheme();
  const categories = useMemo(() => statusByMonth?.categories ?? [], [statusByMonth]);
  const rawSeries = useMemo(() => statusByMonth?.series ?? [], [statusByMonth]);

  const series = useMemo(
    () => rawSeries.map((item) => ({ name: statusLabels[item.name] ?? item.name, data: item.data ?? [] })),
    [rawSeries, statusLabels],
  );

  const isSingleMonth = categories.length === 1;

  const options = useMemo(() => {
    const colors = rawSeries.map((item) => getStatusColor(resolvedTheme, item.name, { statusOrder, statusTones }));

    return {
      chart: {
        type: 'bar',
        stacked: true,
        stackType: isSingleMonth ? '100%' : 'normal',
        background: 'transparent',
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit',
        events: {
          dataPointSelection: (_event, _context, { seriesIndex, dataPointIndex }) => {
            const status = rawSeries[seriesIndex]?.name;
            const month = categories[dataPointIndex];
            if (onBarClick && status && month) {
              onBarClick({ status, month });
            }
          },
        },
      },
      theme: { mode: resolvedTheme },
      colors,
      plotOptions: {
        bar: isSingleMonth
          ? { horizontal: true, barHeight: '40%' }
          : {
            horizontal: false,
            columnWidth: '45%',
            borderRadius: 4,
            borderRadiusApplication: 'end',
            borderRadiusWhenStacked: 'last',
            dataLabels: {
              total: {
                enabled: true,
                formatter: (value) => formatCount(value),
                style: { fontSize: '12px', fontWeight: 600 },
              },
            },
          },
      },
      // 單月比例條在段內標筆數（隱藏 0）；多月只在柱頂標總數
      dataLabels: isSingleMonth
        ? {
          enabled: true,
          formatter: (_value, { seriesIndex, dataPointIndex, w }) => {
            const count = w.config.series[seriesIndex]?.data?.[dataPointIndex] ?? 0;
            return count > 0 ? formatCount(count) : '';
          },
        }
        : { enabled: false },
      stroke: { show: true, width: 1, colors: ['transparent'] },
      xaxis: isSingleMonth
        ? { categories, labels: { formatter: (value) => `${Math.round(value)}%` } }
        : { categories },
      yaxis: isSingleMonth
        ? {}
        : {
          min: 0,
          forceNiceScale: true,
          labels: { formatter: (value) => formatCount(Math.round(value)) },
        },
      grid: { strokeDashArray: 3 },
      legend: { show: false },
      tooltip: {
        shared: true,
        intersect: false,
        custom: ({ series: values, dataPointIndex }) => {
          const rows = series
            .map((item, index) => ({
              name: item.name,
              color: colors[index],
              value: values[index]?.[dataPointIndex] ?? 0,
            }))
            .filter((row) => row.value > 0)
            .map((row) => `
              <div class="flex items-center gap-2 px-3 py-0.5">
                <span class="size-2 shrink-0 rounded-full" style="background:${row.color}"></span>
                <span>${row.name}</span>
                <span class="ml-auto pl-3 font-semibold tabular-nums">${formatCount(row.value)} 筆</span>
              </div>`)
            .join('');
          return `
            <div class="py-1.5 text-xs">
              <div class="px-3 pb-1 text-muted-foreground">${categories[dataPointIndex] ?? ''}</div>
              ${rows || '<div class="px-3 py-0.5 text-muted-foreground">無單據</div>'}
            </div>`;
        },
      },
      states: { active: { filter: { type: 'none' } } },
    };
  }, [categories, isSingleMonth, onBarClick, rawSeries, resolvedTheme, series, statusOrder, statusTones]);

  if (categories.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">此區間沒有資料</p>;
  }

  return (
    <div className={cn(DASHBOARD_CHART_CLASS, onBarClick && '[&_.apexcharts-bar-area]:cursor-pointer')}>
      <Chart
        key={`${resolvedTheme}-${isSingleMonth ? 'single' : 'months'}`}
        type="bar"
        height={isSingleMonth ? 160 : height}
        series={series}
        options={options}
      />
    </div>
  );
};

StatusByMonthChart.propTypes = {
  statusByMonth: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.string),
    series: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number),
      }),
    ),
  }),
  statusLabels: PropTypes.object.isRequired,
  statusOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
  statusTones: PropTypes.objectOf(PropTypes.string),
  onBarClick: PropTypes.func,
  height: PropTypes.number,
};

export default StatusByMonthChart;
